import { Button, Tooltip, TooltipContent, TooltipTrigger } from '@monksflow/monks-ui';
import type { LucideIcon } from 'lucide-react';
import clsx from 'clsx';

interface Props {
  Icon: LucideIcon;
  label: string;
  /**
   * Tooltip text. Defaults to `label`.
   */
  tooltip?: string;
  onClick?: () => void;
  disabled?: boolean;
  /**
   * When provided, the button renders as a toggle: aria-pressed reflects
   * `active` and the variant switches to `secondary` when on. Omit for
   * one-shot action buttons (Undo, Clear, Attach, …).
   */
  active?: boolean;
  /**
   * Visual size. `md` (default) is the popover/toolbar size; `sm` is the
   * tighter inline-actions-bar size.
   */
  size?: 'sm' | 'md';
}

const SIZE_CLASSES = {
  sm: 'size-8 p-0',
  md: 'size-10 rounded-md p-0',
} as const;

const ICON_CLASSES = {
  sm: 'size-4',
  md: 'size-5',
} as const;

export function IconActionButton({
  Icon,
  label,
  tooltip,
  onClick,
  disabled,
  active,
  size = 'md',
}: Props) {
  const isToggle = active !== undefined;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant={isToggle && active ? 'secondary' : 'ghost'}
          size="mini"
          className={clsx(SIZE_CLASSES[size])}
          aria-label={label}
          aria-pressed={isToggle ? active : undefined}
          onClick={onClick}
          disabled={disabled}
        >
          <Icon className={ICON_CLASSES[size]} />
        </Button>
      </TooltipTrigger>
      <TooltipContent>{tooltip ?? label}</TooltipContent>
    </Tooltip>
  );
}
